<script lang="ts">
  import {
    GROUP_LABELS,
    getLayerDef,
    selectableLayers,
    type LayerDef,
    type LayerGroup as Group,
  } from '../map/layers/registry'
  import {
    isActive,
    layers,
    moveLayer,
    opacityOf,
    setOpacity,
    toggleLayer,
  } from '../state/layers.svelte'
  import Icon from './Icon.svelte'
  import LayerGroup from './LayerGroup.svelte'
  import ModelPicker from './ModelPicker.svelte'

  /**
   * Toggle, dim and reorder the overlay stack, as round 2 draws it (Stage
   * 10.5, 2a). The desktop panel lays the groups out in two columns and the
   * phone sheet in one; the rows are the same.
   *
   * Opacity sits on the row of a layer that is on, rather than in a separate
   * stack list, because that is where somebody looking at a layer goes to dim
   * it. The order still matters (alerts under radar hide the alerts), so a
   * short draw-order list follows the groups when two or more layers are on:
   * the handoff drew no reordering at all, and the owner kept it (2026-09-28).
   */

  let { desktop = false }: { desktop?: boolean } = $props()

  // Order comes from `GROUP_LABELS`, not a second list here: a copy of the
  // group order in this file is a place to forget a new group, and a group
  // missing from it is a layer with no way to turn it on.
  const groups = (Object.keys(GROUP_LABELS) as Group[]).map((g) => {
    const defs = selectableLayers().filter((l) => l.group === g)
    return {
      group: g,
      label: GROUP_LABELS[g],
      defs,
      // Every nowCOAST raster is exclusive within its group, so Satellite and
      // NDFD were one-at-a-time behind checkboxes. A radio list with a None
      // row says what the group does, the way the handoff draws SPC outlooks.
      // A group of one keeps its checkbox, which is the same control there.
      radio: g !== 'models' && defs.length > 1 && defs.every((d) => d.exclusive),
    }
  })

  /** The desktop's two columns break after Models, as the handoff splits them. */
  const split = groups.findIndex((g) => g.group === 'models') + 1
  let columns = $derived(desktop ? [groups.slice(0, split), groups.slice(split)] : [groups])

  function activeIn(defs: LayerDef[]): LayerDef[] {
    return defs.filter((d) => isActive(d.id))
  }

  /**
   * Which groups start open. On the phone, the ones with something on, so the
   * sheet opens on what is showing and the rest is a list of headers. On the
   * desktop, all of them — except Models, whose picker is tall and opens only
   * when a field is on, and then the groups above it in its column start
   * closed so the picker is in view.
   */
  function initialOpen(): Record<string, boolean> {
    const modelOn = activeIn(groups.find((g) => g.group === 'models')?.defs ?? []).length > 0
    return Object.fromEntries(
      groups.map((g, i) => {
        const on = activeIn(g.defs).length > 0
        if (!desktop) return [g.group, on]
        if (g.group === 'models') return [g.group, modelOn]
        return [g.group, !(modelOn && i < split)]
      }),
    )
  }

  let open = $state(initialOpen())

  /**
   * The two clocks, said where the switch would happen (see `Timeline` in the
   * registry). Turning radar on with HRRR showing drops HRRR, and nothing on
   * screen says why unless the menu does, so each side names what the other
   * would turn off, by its own label.
   */
  let onClock = $derived.by(() => {
    const defs = layers.active.map((id) => getLayerDef(id)).filter((d): d is LayerDef => !!d)
    return {
      live: defs.filter((d) => d.timeline === 'live').map((d) => d.label),
      run: defs.filter((d) => d.timeline === 'run').map((d) => d.label),
    }
  })

  const firstLive = groups.find((g) => g.defs.some((d) => d.timeline === 'live'))?.group

  function noteFor(group: Group): string {
    if (group === 'models' && onClock.live.length > 0) {
      return `Radar and model layers run on different clocks. Turning on a model field turns ${joined(onClock.live)} off.`
    }
    if (group === firstLive && onClock.run.length > 0) {
      return `Radar and models run on different clocks. Turning on a live layer turns ${joined(onClock.run)} off.`
    }
    return ''
  }

  function joined(labels: string[]): string {
    return labels.length <= 1 ? (labels[0] ?? '') : `${labels.slice(0, -1).join(', ')} and ${labels.at(-1)}`
  }

  // Top of the stack first, which is how the map reads from above.
  let stack = $derived([...layers.active].reverse())

  function pct(id: string): number {
    return Math.round(opacityOf(id) * 100)
  }
</script>

{#snippet opacity(def: LayerDef, width: number)}
  <span class="opacity">
    <input
      type="range"
      class="wx-range"
      style:width="{width}px"
      style:--pct="{pct(def.id)}%"
      min="0"
      max="100"
      step="5"
      value={pct(def.id)}
      aria-label="{def.label} opacity"
      oninput={(e) => setOpacity(def.id, Number(e.currentTarget.value) / 100)}
    />
    <span class="pct">{pct(def.id)}%</span>
  </span>
{/snippet}

<div class="layer-control" class:desktop>
  <div class="columns">
    {#each columns as column, c (c)}
      <div class="column">
        {#each column as { group, label, defs, radio } (group)}
          <LayerGroup
            name={label}
            count={activeIn(defs).length}
            exclusive={radio}
            note={noteFor(group)}
            bind:open={open[group]}
          >
            {#if group === 'models'}
              <!-- Thirty-nine rows is not a control. The rows stay — a
                   persisted id still validates synchronously against the
                   static registry — and only the rendering changes. -->
              <ModelPicker />
            {:else}
              <div class="rows" role={radio ? 'radiogroup' : undefined} aria-label={radio ? label : undefined}>
                {#if radio}
                  <label class="row touch-target">
                    <input
                      type="radio"
                      class="wx-box"
                      name="layer-group-{group}"
                      checked={activeIn(defs).length === 0}
                      onchange={() => activeIn(defs).forEach((d) => toggleLayer(d.id))}
                    />
                    <span class="name">None</span>
                  </label>
                {/if}
                {#each defs as def (def.id)}
                  <div class="row">
                    <label class="pick touch-target">
                      <input
                        type={radio ? 'radio' : 'checkbox'}
                        class="wx-box"
                        name={radio ? `layer-group-${group}` : undefined}
                        checked={isActive(def.id)}
                        onchange={() => toggleLayer(def.id)}
                      />
                      <span class="name">{def.label}</span>
                    </label>
                    {#if isActive(def.id)}{@render opacity(def, 72)}{/if}
                  </div>
                {/each}
              </div>
            {/if}
          </LayerGroup>
        {/each}
      </div>
    {/each}
  </div>

  {#if stack.length > 1}
    <section class="order" aria-label="Draw order">
      <div class="order-head">
        <span class="eyebrow">Draw order</span>
        <span class="hint">Top of the list draws on top</span>
      </div>
      {#each stack as id, i (id)}
        {@const def = getLayerDef(id)}
        {#if def}
          <div class="order-row">
            <span class="name">{def.label}</span>
            <button
              type="button"
              class="step touch-target"
              aria-label="Move {def.label} up"
              disabled={i === 0}
              onclick={() => moveLayer(id, 1)}><Icon name="arrow-up" size={16} /></button
            >
            <button
              type="button"
              class="step touch-target"
              aria-label="Move {def.label} down"
              disabled={i === stack.length - 1}
              onclick={() => moveLayer(id, -1)}><Icon name="arrow-down" size={16} /></button
            >
          </div>
        {/if}
      {/each}
    </section>
  {/if}
</div>

<style>
  .layer-control {
    /* The handoff's two densities. The phone's rows are touch rows. */
    --row: 44px;
    --fs: 15px;
    --hd: 44px;
    --px: 20px;
  }

  .layer-control.desktop {
    --row: 32px;
    --fs: 13px;
    --hd: 40px;
    --px: 16px;
  }

  .columns {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
  }

  /* Two columns only when the panel is wide enough to hold them: a desktop
     sidebar beside a 1000 px window leaves a map narrower than the panel. */
  .desktop .columns {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  }

  .desktop .column + .column {
    border-left: 2px solid var(--color-divider);
  }

  .rows {
    display: flex;
    flex-direction: column;
  }

  .row {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: var(--row);
    padding: 0 var(--px);
  }

  /* The row's label is the target, so a tap anywhere left of the slider
     toggles the layer. */
  .pick,
  label.row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;
    min-height: var(--row);
    cursor: pointer;
  }

  .name {
    flex: 1;
    min-width: 0;
    font-size: var(--fs);
    line-height: 1.25;
  }

  .opacity {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: none;
  }

  .pct {
    width: 30px;
    font-size: 11px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  .order {
    padding: 0 0 8px;
  }

  .order-head {
    display: flex;
    align-items: baseline;
    gap: 10px;
    height: var(--hd);
    align-items: center;
    padding: 0 var(--px);
  }

  .hint {
    font-size: 11px;
    color: var(--color-muted);
  }

  .order-row {
    display: flex;
    align-items: center;
    gap: 4px;
    min-height: var(--row);
    padding: 0 calc(var(--px) - 6px) 0 var(--px);
    border-top: 1px solid var(--color-divider);
  }

  .order-row .name {
    font-size: var(--fs);
  }

  .step {
    width: 32px;
    height: 32px;
    display: grid;
    place-items: center;
    border: 0;
    background: none;
    cursor: pointer;
  }

  .step:not(:disabled):hover {
    background: color-mix(in srgb, var(--color-text) 7%, transparent);
  }

  .step:disabled {
    opacity: 0.35;
    cursor: default;
  }
</style>
